/*
  Infinite pagination - cross-browser implementation.

  2026-07-28. Replaces the original button element, which put its target URL in a
  non-standard action attribute that crawlers cannot follow and that left
  collections unusable without JS.

  WHY THIS IS NOT A CUSTOMIZED BUILT-IN ELEMENT
  ---------------------------------------------
  The obvious fix - an anchor with is="infinite-link" - does not work in Safari.
  WebKit's official standards position on customized built-in elements (the is
  attribute form) is "oppose", and it is not implemented:
  https://github.com/WebKit/standards-positions/issues/97
  The theme ships no polyfill (verified: none in vendor.js, theme.js,
  collection.js, cart.js or search.js), so in Safari such an anchor would never
  upgrade and the click handler would never attach.

  This implementation uses a plain anchor plus a single delegated click listener
  on document. That works in every browser, needs no custom element
  registration, and degrades to ordinary link navigation when JS is off or has
  not loaded yet.

  Behaviour parity with the original InfiniteButton is preserved:
    - mode=infinite auto-loads when the control scrolls into view
    - mode=click    loads on click only
    - fetches the next page as a section render and appends the new cards
    - swaps in the next page's pagination markup

  Modified clicks (cmd/ctrl/shift/alt/middle) fall through to native behaviour
  so open-in-new-tab keeps working.
*/
(function () {
  'use strict';

  var SELECTOR = '.pagination a.infinite-scroll';
  var inFlight = null;
  var observed = new WeakSet();

  function sectionIdFor(el) {
    if (window.theme && theme.utils && typeof theme.utils.sectionId === 'function') {
      return theme.utils.sectionId(el);
    }
    var section = el.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function buildUrl(el) {
    // Prefer the absolute action attribute kept from the original markup;
    // fall back to href so the control still works without it.
    var target = el.getAttribute('action') || el.getAttribute('href');
    var url = new URL(target, window.location.origin);
    var id = sectionIdFor(el);
    if (id) url.searchParams.set('section_id', id);
    return url.toString();
  }

  function renderPagination(parsedHTML) {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var destination = container.querySelector('.pagination');
    if (!destination) return;

    var source = parsedHTML.querySelector('.pagination');
    if (source) {
      destination.innerHTML = source.innerHTML;
      observeVisible();
    } else {
      destination.remove();
    }
  }

  function renderProducts(parsedHTML) {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var destination = container.querySelector('motion-list');
    var source = parsedHTML.querySelector('motion-list');
    if (!source || !destination) return;

    source.querySelectorAll('.card').forEach(function (item) {
      destination.appendChild(item);
    });

    if (typeof destination.reload === 'function') destination.reload();
  }

  function load(el) {
    if (el.hasAttribute('aria-busy')) return;

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    el.classList.add('pointer-events-none');
    el.setAttribute('aria-busy', 'true');

    fetch(buildUrl(el), { signal: inFlight.signal })
      .then(function (response) { return response.text(); })
      .then(function (text) {
        var parsed = new DOMParser().parseFromString(text, 'text/html');
        renderPagination(parsed);
        renderProducts(parsed);
      })
      .catch(function (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          // Re-enable so the user can retry, or simply follow the link.
          el.classList.remove('pointer-events-none');
          el.removeAttribute('aria-busy');
        }
      });
  }

  // Delegated click - survives the pagination markup being replaced.
  document.addEventListener('click', function (event) {
    var el = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!el) return;

    // Let modified clicks navigate natively (new tab, etc.).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;

    event.preventDefault();
    load(el);
  });

  // mode=infinite: auto-load when the control comes into view.
  function observeVisible() {
    var el = document.querySelector(SELECTOR);
    if (!el || el.getAttribute('mode') !== 'infinite') return;
    if (observed.has(el)) return;
    observed.add(el);

    if (window.Motion && typeof Motion.inView === 'function') {
      Motion.inView(el, function () { load(el); }, { margin: '200px 0px 200px 0px' });
      return;
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { io.disconnect(); load(el); }
        });
      }, { rootMargin: '200px 0px 200px 0px' });
      io.observe(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeVisible);
  } else {
    observeVisible();
  }

  // Re-arm after filter/sort re-renders the grid.
  document.addEventListener('collection:reloaded', observeVisible);
})();
